import React from 'react';
import { useParams } from 'react-router-dom';
import GoogleSocialLogin from '../components/Socials/GoogleSocialLogin';
import GoogleCallback from '../components/Socials/GoogleCallback';
import FacebookSocialLogin from '../components/Socials/FacebookSocialLogin';
import FacebookCallback from '../components/Socials/FacebookCallback';
import XSocialLogin from '../components/Socials/XSocialLogin';
import XCallback from '../components/Socials/XCallback';

export type SocialLinkPageComponent = React.ComponentType<{}>;

export interface SocialProviderConfig {
  id: string;
  successType: string;
  errorType: string;
  label: string;
  enabled: boolean;
  icon: React.ReactNode;
  socialLogin?: SocialLinkPageComponent;
  socialCallback?: SocialLinkPageComponent;
}

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const FacebookIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="#1877F2">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const XIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const AppleIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
  </svg>
);


/**
 * Get list of social login providers with dynamic enabled state
 * 
 * @param salla - Salla instance to check provider configuration
 * @returns Array of social provider configurations
 * 
 * To add a new provider:
 * 1. Add an item with (id, successType, errorType, label, icon, socialLogin, socialCallback)
 * 2. Create a redirect page component (e.g., FacebookSocialLogin) and add it to socialLogin field
 * 3. Create a callback component (e.g., FacebookCallback) and add it to socialCallback field
 * 4. In the callback, send a postMessage with the successType or errorType along with the data
 */
export const getSocialProviders = (salla?: any): SocialProviderConfig[] => [
  {
    id: 'google',
    successType: 'GOOGLE_LOGIN_SUCCESS',
    errorType: 'GOOGLE_LOGIN_ERROR',
    label: 'Google',
    enabled: salla?.config.get('login.social.google.enabled') ?? false,
    icon: <GoogleIcon />,
    socialLogin: GoogleSocialLogin,
    socialCallback: GoogleCallback,
  },
  {
    id: 'facebook',
    successType: 'FACEBOOK_LOGIN_SUCCESS',
    errorType: 'FACEBOOK_LOGIN_ERROR',
    label: 'Facebook',
    enabled: salla?.config.get('login.social.facebook.enabled') ?? false,
    icon: <FacebookIcon />,
    socialLogin: FacebookSocialLogin,
    socialCallback: FacebookCallback,
  },
  {
    id: 'x',
    successType: 'X_LOGIN_SUCCESS',
    errorType: 'X_LOGIN_ERROR',
    label: 'X',
    enabled: salla?.config.get('login.social.x.enabled') ?? false,
    icon: <XIcon />,
    socialLogin: XSocialLogin,
    socialCallback: XCallback,
  },
  {
    id: 'apple',
    successType: 'APPLE_LOGIN_SUCCESS',
    errorType: 'APPLE_LOGIN_ERROR',
    label: 'Apple',
    enabled: salla?.config.get('login.social.apple.enabled') ?? false,
    icon: <AppleIcon />,
    //socialLogin: AppleSocialLogin, Add the component when you create it
    //socialCallback: AppleCallback, Add the component when you create it
  },
];

// For backward compatibility and static usage
export const SOCIAL_PROVIDERS = getSocialProviders();

export const getSocialSuccessTypes = (salla?: any) => getSocialProviders(salla).map((p) => p.successType);
export const getSocialErrorTypes = (salla?: any) => getSocialProviders(salla).map((p) => p.errorType);
export const getEnabledSocialProviders = (salla?: any) => getSocialProviders(salla).filter((p) => p.enabled);

/**
 * Dynamic Route for social login
 * Reads :provider from the URL and emits the appropriate component from SOCIAL_PROVIDERS
 */
export function SocialLoginRoute() {
  const { provider } = useParams<{ provider: string }>();
  const providerConfig = SOCIAL_PROVIDERS.find((p) => p.id === provider);
  const SocialLogin = providerConfig?.socialLogin;  
  if (!SocialLogin) return null;
  return <SocialLogin />;
}

/**
 * Dynamic Route for social callback
 * Reads :provider from the URL and emits the appropriate component from SOCIAL_PROVIDERS
 */
export function SocialCallbackRoute() {
  const { provider } = useParams<{ provider: string }>();
  const providerConfig = SOCIAL_PROVIDERS.find((p) => p.id === provider);
  const CallbackComponent = providerConfig?.socialCallback;  
  if (!CallbackComponent) return null;
  return <CallbackComponent />;
}
